import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  Checkbox,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Upload as UploadIcon,
  FileUpload as FileUploadIcon,
  Download as DownloadIcon,
  Contacts as ContactsIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import AddressDialog from './AddressDialog';
import { getAuthHeaders } from '../../utils/authUtils';

const AddressManagement = () => {
  const { t } = useTranslation();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAddresses, setSelectedAddresses] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [currentAddress, setCurrentAddress] = useState(null);
  const [confirmAction, setConfirmAction] = useState({ open: false, title: '', description: '', onConfirm: null });
  const [csvFile, setCsvFile] = useState(null);
  const [csvUploading, setCsvUploading] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:25000';

  const loadAddresses = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/addresses`, {
        headers: getAuthHeaders(),
        params: {
          search: searchTerm || undefined
        }
      });
      
      setAddresses(response.data);
    } catch (err) {
      console.error('Error loading addresses:', err);
      setError(err.response?.data?.detail || t('admin.addressManagement.loadError'));
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    loadAddresses();
  }, [loadAddresses]);

  const filteredAddresses = addresses.filter(address => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      address.referring_physician?.toLowerCase().includes(searchLower) ||
      address.first_name?.toLowerCase().includes(searchLower) ||
      address.last_name?.toLowerCase().includes(searchLower) ||
      address.organization?.toLowerCase().includes(searchLower) ||
      address.city?.toLowerCase().includes(searchLower)
    );
  });

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedAddresses(filteredAddresses.map(address => address.id));
    } else {
      setSelectedAddresses([]);
    }
  };

  const handleSelectAddress = (addressId) => {
    setSelectedAddresses(prev => 
      prev.includes(addressId) 
        ? prev.filter(id => id !== addressId)
        : [...prev, addressId]
    );
  };

  const handleOpenDialog = (address = null) => {
    setCurrentAddress(address);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setCurrentAddress(null);
  };

  const handleSaveAddress = async (addressData) => {
    try {
      if (currentAddress) {
        // Update existing address
        await axios.put(`${API_BASE_URL}/admin/addresses/${currentAddress.id}`, addressData, {
          headers: getAuthHeaders()
        });
        setSuccessMessage(t('admin.addressManagement.saveSuccess'));
      } else {
        // Create new address
        await axios.post(`${API_BASE_URL}/admin/addresses`, addressData, {
          headers: getAuthHeaders()
        });
        setSuccessMessage(t('admin.addressManagement.createSuccess'));
      }
      
      handleCloseDialog();
      loadAddresses();
      
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Error saving address:', err);
      setError(err.response?.data?.detail || t('admin.addressManagement.saveError'));
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleDeleteSelected = () => {
    if (selectedAddresses.length === 0) return;

    setConfirmAction({
      open: true,
      title: t('admin.addressManagement.deleteTitle'),
      description: t('admin.addressManagement.deleteConfirm', { count: selectedAddresses.length }),
      onConfirm: async () => {
        try {
          await axios.post(`${API_BASE_URL}/admin/addresses/bulk-delete`, selectedAddresses, {
            headers: getAuthHeaders()
          });
          
          setSuccessMessage(t('admin.addressManagement.deleteSuccess', { count: selectedAddresses.length }));
          setSelectedAddresses([]);
          loadAddresses();
          setTimeout(() => setSuccessMessage(''), 5000);
        } catch (err) {
          console.error('Error deleting addresses:', err);
          setError(err.response?.data?.detail || t('admin.addressManagement.deleteError'));
          setTimeout(() => setError(''), 5000);
        }
        setConfirmAction({ open: false, title: '', description: '', onConfirm: null });
      }
    });
  };

  const handleCsvUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setCsvUploading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/admin/addresses/upload-csv`, formData, {
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'multipart/form-data'
        }
      });

      const message = response.data.imported > 0 
        ? t('admin.addressManagement.uploadSuccess', { count: response.data.imported })
        : t('admin.addressManagement.uploadNoNew');
      setSuccessMessage(message);
      loadAddresses();
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Error uploading CSV:', err);
      setError(err.response?.data?.detail || t('admin.addressManagement.uploadError'));
      setTimeout(() => setError(''), 5000);
    } finally {
      setCsvUploading(false);
      event.target.value = ''; // Reset file input
    }
  };

  const handleCsvTemplateDownload = () => {
    // CSV Header definieren
    const headers = [
      'referring_physician',
      'salutation', 
      'title',
      'first_name',
      'last_name',
      'organization',
      'street',
      'postal_code',
      'city',
      'country',
      'gender'
    ];

    // Translated headers for user-friendliness
    const translatedHeaders = [
      t('admin.addressManagement.csvHeaders.designation'),
      t('admin.addressManagement.csvHeaders.salutation'),
      t('admin.addressManagement.csvHeaders.title'),
      t('admin.addressManagement.csvHeaders.firstName'),
      t('admin.addressManagement.csvHeaders.lastName'),
      t('admin.addressManagement.csvHeaders.organization'),
      t('admin.addressManagement.csvHeaders.street'),
      t('admin.addressManagement.csvHeaders.postalCode'),
      t('admin.addressManagement.csvHeaders.city'),
      t('admin.addressManagement.csvHeaders.country'),
      t('admin.addressManagement.csvHeaders.gender')
    ];

    // CSV Inhalt erstellen mit BOM für korrekte Darstellung in Excel
    const csvContent = '\uFEFF' + [
      headers.join(';'), // Technical headers (processed by backend)
      '# ' + translatedHeaders.join(';'), // Translated headers as comment line
      '# ' + t('admin.addressManagement.csvExample'),
      '# ' + t('admin.addressManagement.csvGenderHint'),
      '# ' + t('admin.addressManagement.csvSkipExisting'),
      ''
    ].join('\n');

    // CSV Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', t('admin.addressManagement.csvTemplateFilename'));
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getGenderLabel = (gender) => {
    switch (gender) {
      case 'weiblich': return t('admin.addressManagement.genderLabels.female');
      case 'männlich': return t('admin.addressManagement.genderLabels.male');
      case 'divers': return t('admin.addressManagement.genderLabels.diverse');
      default: return '-';
    }
  };

  const getFullName = (address) => {
    const parts = [];
    if (address.title) parts.push(address.title);
    if (address.first_name) parts.push(address.first_name);
    if (address.last_name) parts.push(address.last_name);
    return parts.join(' ') || '-';
  };

  const getFullAddress = (address) => {
    const parts = [];
    if (address.street) parts.push(address.street);
    if (address.postal_code || address.city) {
      const cityPart = [address.postal_code, address.city].filter(Boolean).join(' ');
      parts.push(cityPart);
    }
    if (address.country) parts.push(address.country);
    return parts.join(', ') || '-';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ContactsIcon color="primary" /> {t('admin.addressManagement.title')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Header mit Action-Buttons */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              className="button"
              disabled={loading || csvUploading}
            >
              {t('admin.addressManagement.addAddress')}
            </Button>
            <Button
              className="button"
              variant="contained"
              startIcon={<UploadIcon />}
              component="label"
              disabled={csvUploading || loading}
            >
              {csvUploading ? <CircularProgress size={20} /> : t('admin.addressManagement.csvImport')}
              <input
                type="file"
                accept=".csv"
                hidden
                onChange={handleCsvUpload}
              />
            </Button>
            <Button
              className="button"
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleCsvTemplateDownload}
              disabled={csvUploading || loading}
            >
              {t('admin.addressManagement.csvTemplate')}
            </Button>
          </Box>
        </Box>

        {/* Selection und Filter Toolbar */}
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" color="text.secondary">
              {selectedAddresses.length > 0
                ? t('admin.addressManagement.selectedCount', { count: selectedAddresses.length })
                : t('admin.addressManagement.noSelected')
              }
            </Typography>
            <Divider orientation="vertical" flexItem />
            <Tooltip title={t('common.edit')}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => {
                    if (selectedAddresses.length === 1) {
                      const address = addresses.find(a => a.id === selectedAddresses[0]);
                      handleOpenDialog(address);
                    }
                  }}
                  disabled={selectedAddresses.length !== 1 || loading || csvUploading}
                >
                  <EditIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('common.delete')}>
              <span>
                <IconButton
                  size="small"
                  color="error"
                  onClick={handleDeleteSelected}
                  disabled={selectedAddresses.length === 0 || loading || csvUploading}
                >
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          
          {/* Filter-Bereich */}
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <TextField
              label={t('admin.addressManagement.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ minWidth: 220 }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
              }}
            />
            <Tooltip title={t('common.refresh')}>
              <IconButton
                onClick={loadAddresses}
                disabled={loading || csvUploading}
                size="small"
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      {/* Address Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedAddresses.length > 0 && selectedAddresses.length < filteredAddresses.length}
                  checked={filteredAddresses.length > 0 && selectedAddresses.length === filteredAddresses.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell>{t('admin.addressManagement.tableHeaders.designation')}</TableCell>
              <TableCell>{t('admin.addressManagement.tableHeaders.name')}</TableCell>
              <TableCell>{t('admin.addressManagement.tableHeaders.organization')}</TableCell>
              <TableCell>{t('admin.addressManagement.tableHeaders.address')}</TableCell>
              <TableCell>{t('admin.addressManagement.tableHeaders.gender')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {t('admin.addressManagement.loading')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredAddresses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {searchTerm ? t('admin.addressManagement.noResults') : t('admin.addressManagement.noAddresses')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredAddresses.map((address) => (
                <TableRow key={address.id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedAddresses.includes(address.id)}
                      onChange={() => handleSelectAddress(address.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {address.referring_physician}
                    </Typography>
                    {address.salutation && (
                      <Typography variant="caption" color="text.secondary">
                        {address.salutation}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {getFullName(address)}
                  </TableCell>
                  <TableCell>
                    {address.organization || '-'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {getFullAddress(address)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {address.gender && (
                      <Chip
                        size="small"
                        label={getGenderLabel(address.gender)}
                        color={
                          address.gender === 'weiblich' ? 'secondary' :
                          address.gender === 'männlich' ? 'primary' : 'default'
                        }
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Address Dialog */}
      <AddressDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        onSave={handleSaveAddress}
        address={currentAddress}
      />

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmAction.open}
        onClose={() => setConfirmAction({ open: false, title: '', description: '', onConfirm: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{confirmAction.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmAction.description}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmAction({ open: false, title: '', description: '', onConfirm: null })}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={confirmAction.onConfirm}
            color="error"
            variant="contained"
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AddressManagement;
